import { useRequest } from "ahooks";
import { request } from "../../request";
import { getCustomerId } from "../../Common";
import {
  IS_IFRAMES,
  getIframesToken,
  PATH_CUSTOMER_ID,
} from "MortgageGPT/constants";

export interface TypeGuideline {
  id: string;
  /**
   * @param displayName 显示名称
   * @description AmeriHome Non-QM DSCR
   */
  displayName: string;
  /**
   * @param programName 应用名
   * @description amerihome_dscr
   */
  programName: string;
  /**
   * @param keyWords 关联的tag
   */
  keyWords: string[];
  /**
   * @param lenderDisplayName 对应的 lender 显示名称
   */
  lenderDisplayName: string;
  /**
   * @param lenderDisplayName 对应的 lender id
   */
  lenderId: string;
  /**
   * @param lenderName 对应的 lender 应用名称
   */
  lenderName: string;
  /**
   * @param available 用户是否可用
   * @description 根据 “available_documents” 返回，true 则意味着用户有该权限，可以使用
   */
  available: boolean;
  fileName: string;
  filePath: string;
  docType: string;
}

/**
 * @name TypeTag 关联 “Guideline” 的tag
 * @description 一个tag，会关联多个 “Guideline”
 */
export interface TypeTag {
  id: number;
  name: string;
  customer?: string;
  /**
   * @param documents 关联的 “Guideline” ID
   */
  documents: string[];
  /**
   * @param isDefault 是否默认，默认不能删除
   */
  isDefault: boolean;
}

export interface TypePrograms<
  T extends TypeGuideline = TypeGuideline,
  R = { options: T[] } & Pick<
    TypeGuideline,
    "lenderId" | "lenderName" | "lenderDisplayName"
  >,
> {
  /**
   * @param MAP_TAGS 通过 tag ID 映射 tag 信息
   */
  MAP_TAGS: Record<TypeTag["id"], TypeTag>;
  /**
   * @param MAP_LENDER_ID 通过 “lenderId” 映射 lender
   */
  MAP_LENDER_ID: Record<string, T>;
  /**
   * @param MAP_LENDER_NAME 通过 “lenderName” 映射 lender
   */
  MAP_LENDER_NAME: Record<string, T>;
  /**
   * @param MAP_LENDER_PROGRAM_ID 通过 “program id” 映射 lender
   */
  MAP_LENDER_PROGRAM_ID: Record<string, T>;
  /**
   * @param MAP_GUIDELINE_ID 通过 “guideline Id” 映射 guideline
   */
  MAP_GUIDELINE_ID: Record<string, T>;
  /**
   * @param MAP_GUIDELINE_FILE_PATH 通过 “filePath” 映射 guideline
   */
  MAP_GUIDELINE_FILE_PATH: Record<string, T>;
  /**
   * @param MAP_GUIDELINE_FILE_NAME 通过 “fileName” 映射 guideline
   */
  MAP_GUIDELINE_FILE_NAME: Record<string, T>;
  /**
   * @param MAP_GROUP_GUIDELINES 通过 lenderId 进行分组、映射
   */
  MAP_GROUP_GUIDELINES: Record<string, R>;
  /**
   * @param LIST_GROUP_GUIDELINES 通过 lenderId 进行分组
   */
  LIST_GROUP_GUIDELINES: R[];
  GUIDELINES: T[];
  GUIDELINES_ID: Array<T["id"]>;
  TAGS: Array<TypeTag>;
  /**
   * @param IS_LOGGED 当前用户是否登陆
   */
  IS_LOGGED: boolean;
}

const DEFAULT_VALUE = {
  TAGS: [],
  IS_LOGGED: false,
  GUIDELINES: [],
  GUIDELINES_ID: [],
  MAP_LENDER_ID: {},
  MAP_LENDER_NAME: {},
  MAP_GUIDELINE_ID: {},
  MAP_GROUP_GUIDELINES: {},
  MAP_LENDER_PROGRAM_ID: {},
  LIST_GROUP_GUIDELINES: [],
};

/**
 * @name useAllPrograms 获取 Scenario AI  “Tag”、“Guideline” 数据
 * @description 仅聚焦 “Tag”、“Guideline” 数据结构处理
 * @example
 *  const [tags, guidelines] = await Promise.all([
 *    request(`/api/v1/mortgagegpt/listSessionTags`), // tag
 *    request("/api/v1/mortgagegpt/listAllPrograms"), // guidelines
 *  ]);
 */
export function useAllPrograms(activeIframeSession: string) {
  // Track customerId to trigger refresh when it changes (e.g., after login)
  const CUSTOMER_ID = getCustomerId();
  const IS_LOGGED = Boolean(
    IS_IFRAMES ? localStorage.getItem(getIframesToken()) : CUSTOMER_ID,
  );

  const { data, run, runAsync, mutate, loading, error } = useRequest(
    async () => {
      // CUSTOMER_ID 会提供给后端进行过滤 "guideline"
      // 若为 "IFRAMES" 模式，使用 "PATH_CUSTOMER_ID" 其他场景使用 "CUSTOMER_ID"
      const customerId = IS_IFRAMES ? PATH_CUSTOMER_ID : getCustomerId();
      const query = customerId ? `?customerId=${customerId}` : "";

      const [tags, guidelines] = await Promise.all([
        request(`/api/v1/mortgagegpt/listSessionTags${query}`), // tag
        request(`/api/v1/mortgagegpt/listAllPrograms${query}`), // guidelines
      ]);

      const TAGS = [];
      const GUIDELINES = [];

      let GUIDELINES_ID = []; // 用户拥有的 guidelines id，preview模式下会频繁用到，这里一次性处理

      const MAP_TAGS = {}; // tag 映射

      const MAP_LENDER_ID = {}; // lender ID 映射
      const MAP_LENDER_NAME = {}; // lender name 映射
      const MAP_LENDER_PROGRAM_ID = {}; // // program id 映射

      const MAP_GUIDELINE_ID = {}; // ID 映射 MAP_PROGRAM
      const MAP_GUIDELINE_FILE_PATH = {}; // filePath 映射 guideline
      const MAP_GUIDELINE_FILE_NAME = {}; // fileName 映射 guideline
      const MAP_GROUP_GUIDELINES = {}; // 将同类型的“guideline”分组展示

      // 用户可预览的 guidelines
      const PRESENTABLE_GUIDELINE_IDS = new Set(
        guidelines.data?.available_documents,
      );

      // 处理 guidelines
      for (const v of guidelines.data?.all_documents || []) {
        const available = PRESENTABLE_GUIDELINE_IDS.has(v.id);
        v.available = available; // 该item，是否用户可用，

        MAP_GUIDELINE_ID[v.id] = v;

        MAP_LENDER_ID[v.lenderId] = v;
        MAP_LENDER_PROGRAM_ID[v.id] = v;

        const filePath = v.filePath
          .split("s3://scenarioai-guidelines/")
          ?.at(-1);

        MAP_GUIDELINE_FILE_PATH[filePath] = v;
        MAP_GUIDELINE_FILE_NAME[v.fileName] = v;

        MAP_LENDER_NAME[v.lenderDisplayName] = v;

        // 不可预览的 “guideline” 不显示
        if (!available || v.isHidden) {
          continue;
        }

        GUIDELINES.push(v);
        // GUIDELINES_ID.push(v.id);

        if (MAP_GROUP_GUIDELINES[v.lenderId]) {
          MAP_GROUP_GUIDELINES[v.lenderId].options.push(v);
        } else {
          MAP_GROUP_GUIDELINES[v.lenderId] = {
            options: [v],
            lenderId: v.lenderId,
            lenderName: v.lenderName,
            lenderDisplayName: v.lenderDisplayName,
          };
        }
      }

      // 排序
      GUIDELINES.sort(
        (a, b) =>
          a.lenderDisplayName.localeCompare(b.lenderDisplayName) ||
          a.displayName.localeCompare(b.displayName),
      );
      GUIDELINES_ID = GUIDELINES.map((v) => v.id);

      // “listSessionTags”、“istAllPrograms”的 “available_documents” 都是一样的
      const PRESENTABLE_TAG_IDS = new Set(tags?.data?.available_documents);

      // 过滤 tag
      for (const v of tags?.data?.tags || []) {
        // 若该 tag 的 documents 中的任一 id 存在于 available_documents，则保留该 tag，否则过滤掉
        v.documents = v.documents.filter((k: string) =>
          PRESENTABLE_TAG_IDS.has(k),
        );
        MAP_TAGS[v.id] = v;
        v.documents?.length && TAGS.push(v);
      }

      return {
        TAGS,
        MAP_TAGS,
        IS_LOGGED,
        GUIDELINES,
        GUIDELINES_ID,
        MAP_LENDER_ID,
        MAP_LENDER_NAME,
        MAP_GUIDELINE_ID,
        MAP_GROUP_GUIDELINES,
        MAP_LENDER_PROGRAM_ID,
        MAP_GUIDELINE_FILE_PATH,
        MAP_GUIDELINE_FILE_NAME,
        LIST_GROUP_GUIDELINES: Object.values(MAP_GROUP_GUIDELINES),
      } as TypePrograms;
    },
    {
      // Refresh when activeIframeSession changes OR when customerId/login status changes
      refreshDeps: [activeIframeSession, CUSTOMER_ID, IS_LOGGED],
    },
  );

  return {
    run,
    error,
    mutate,
    loading,
    runAsync,
    data: (data || DEFAULT_VALUE) as TypePrograms,
  };
}

export type TypeUseAllPrograms = ReturnType<typeof useAllPrograms>;
